import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, RefreshCw, Paperclip, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SoporteModal({ open, onClose }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', description: '' });
  const [files, setFiles] = useState([]);
  const [captcha, setCaptcha] = useState({ captcha_id: '', question: '' });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // ticket_number string when success

  const loadCaptcha = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/support/captcha`);
      setCaptcha(data);
      setCaptchaAnswer('');
    } catch {
      toast.error('No se pudo cargar el captcha. Intenta de nuevo.');
    }
  }, []);

  useEffect(() => {
    if (open) {
      setForm({ name: '', phone: '', email: '', description: '' });
      setFiles([]);
      setSuccess(null);
      loadCaptcha();
    }
  }, [open, loadCaptcha]);

  if (!open) return null;

  const handleFile = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length > 2) {
      toast.error('Máximo 2 archivos adjuntos');
      e.target.value = '';
      return;
    }
    setFiles(selected);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim() || !form.description.trim()) {
      toast.error('Todos los campos son obligatorios');
      return;
    }
    if (!captchaAnswer) {
      toast.error('Por favor responde el captcha');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('phone', form.phone);
      fd.append('email', form.email);
      fd.append('description', form.description);
      fd.append('captcha_id', captcha.captcha_id);
      fd.append('captcha_answer', captchaAnswer);
      files.forEach((f) => fd.append('files', f));
      const { data } = await axios.post(`${API}/support/tickets`, fd);
      setSuccess(data.ticket_number);
      toast.success(`Ticket ${data.ticket_number} creado`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al enviar el ticket';
      toast.error(msg);
      loadCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      data-testid="soporte-modal-backdrop"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.7)', zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 12, maxWidth: 560, width: '100%',
          maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>Centro de Soporte</h2>
            <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#64748b' }}>Crea un ticket PQR y te contactaremos pronto</p>
          </div>
          <button onClick={onClose} data-testid="soporte-close-btn" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b' }}>
            <X size={22} />
          </button>
        </div>

        {success ? (
          <div style={{ padding: '2rem 1.5rem', textAlign: 'center' }} data-testid="soporte-success">
            <CheckCircle2 size={56} color="#10b981" style={{ margin: '0 auto 1rem' }} />
            <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#0f172a', fontWeight: 700 }}>Ticket creado</h3>
            <p style={{ margin: '0.5rem 0 0.25rem', color: '#475569' }}>
              Tu número de radicado es
            </p>
            <div style={{ display: 'inline-block', marginTop: 8, padding: '0.5rem 1.25rem', background: '#f0f9ff', color: '#0369a1', borderRadius: 8, fontSize: '1.5rem', fontWeight: 800, letterSpacing: 1 }}>
              {success}
            </div>
            <p style={{ marginTop: '1.25rem', color: '#64748b', fontSize: '0.9rem' }}>
              Guárdalo para consultar el estado. Nuestro equipo responderá al correo registrado.
            </p>
            <button
              onClick={onClose}
              data-testid="soporte-close-success-btn"
              style={{ marginTop: '1.5rem', background: '#0f172a', color: 'white', border: 'none', padding: '0.65rem 1.5rem', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
            >
              Cerrar
            </button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ padding: '1.25rem 1.5rem' }}>
            <Field label="Nombre completo" required>
              <input
                data-testid="soporte-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                style={inputStyle}
                required
              />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Teléfono" required>
                <input
                  data-testid="soporte-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  style={inputStyle}
                  required
                />
              </Field>
              <Field label="Correo electrónico" required>
                <input
                  data-testid="soporte-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  style={inputStyle}
                  required
                />
              </Field>
            </div>
            <Field label="Descripción del inconveniente" required>
              <textarea
                data-testid="soporte-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                maxLength={5000}
                required
              />
            </Field>

            <Field label="Adjuntos (opcional, máx 2 archivos, 10 MB c/u)">
              <label
                htmlFor="soporte-files"
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '0.65rem 0.85rem',
                  border: '1px dashed #cbd5e1', borderRadius: 6, cursor: 'pointer', background: '#f8fafc',
                  fontSize: '0.9rem', color: '#475569',
                }}
              >
                <Paperclip size={16} />
                {files.length ? files.map(f => f.name).join(', ') : 'Seleccionar archivos…'}
              </label>
              <input
                id="soporte-files"
                data-testid="soporte-files"
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
                multiple
                onChange={handleFile}
                style={{ display: 'none' }}
              />
            </Field>

            <Field label="Verificación" required>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div
                  data-testid="soporte-captcha-question"
                  style={{
                    padding: '0.6rem 0.85rem', background: '#f1f5f9', borderRadius: 6, fontWeight: 700,
                    color: '#0f172a', fontFamily: 'monospace', fontSize: '1.05rem', minWidth: 90, textAlign: 'center',
                  }}
                >
                  {captcha.question || '…'} = ?
                </div>
                <input
                  data-testid="soporte-captcha-answer"
                  type="number"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  placeholder="Resultado"
                  style={{ ...inputStyle, flex: 1 }}
                  required
                />
                <button
                  type="button"
                  onClick={loadCaptcha}
                  data-testid="soporte-captcha-refresh"
                  title="Cambiar captcha"
                  style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', padding: '0.5rem', borderRadius: 6, cursor: 'pointer' }}
                >
                  <RefreshCw size={16} />
                </button>
              </div>
            </Field>

            <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
              <button
                type="button"
                onClick={onClose}
                style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', padding: '0.7rem', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                data-testid="soporte-submit"
                disabled={submitting}
                style={{
                  flex: 2, background: submitting ? '#94a3b8' : '#0f172a', color: 'white',
                  border: 'none', padding: '0.7rem', borderRadius: 6, cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 600
                }}
              >
                {submitting ? 'Enviando…' : 'Enviar ticket'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: 6,
  fontSize: '0.95rem', color: '#0f172a', outline: 'none', boxSizing: 'border-box',
};

const Field = ({ label, required, children }) => (
  <div style={{ marginBottom: '0.9rem' }}>
    <label style={{ display: 'block', marginBottom: 6, fontSize: '0.85rem', color: '#334155', fontWeight: 500 }}>
      {label} {required && <span style={{ color: '#dc2626' }}>*</span>}
    </label>
    {children}
  </div>
);
